import React, { useEffect, useId, useMemo, useState } from "react";
import styled from "styled-components";
import { Cancel, DocFile, Docx, FileUpload, Pdf } from "../../styled/AllImages";
import { useTranslation } from "react-i18next";

const IMAGE_EXT = new Set(["png", "jpg", "jpeg", "webp", "gif"]);

const fileExtension = (name) =>
  String(name || "")
    .split(".")
    .pop()
    .toLowerCase();

/**
 * Document / license file picker. Shows PDF/DOC icons or an image thumbnail.
 * DocFile/Pdf/Docx from AllImages are PNG URL strings — must use <img src={...} />,
 * never <DocFile /> (that crashes React with a white screen).
 *
 * selectedFile may be a real File/Blob, or a hydrate stub `{ name, url }` for
 * an existing remote license (must not call createObjectURL on stubs).
 */
const DocumentUpload = ({
  accept,
  UploadImage,
  UploadSecTxt,
  onChange,
  selectedFile,
  setSelectedFile,
}) => {
  const { t } = useTranslation();
  const inputId = useId();
  const [objectPreviewUrl, setObjectPreviewUrl] = useState(null);

  const isBlob = selectedFile instanceof Blob;
  const remoteUrl =
    typeof selectedFile?.url === "string" && selectedFile.url.trim() !== ""
      ? selectedFile.url.trim()
      : "";

  const isImageFile = useMemo(() => {
    if (!selectedFile) return false;
    const type = String(selectedFile.type || "").toLowerCase();
    if (type.startsWith("image/")) return true;
    return IMAGE_EXT.has(fileExtension(selectedFile.name));
  }, [selectedFile]);

  useEffect(() => {
    if (!isBlob || !isImageFile) {
      setObjectPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return undefined;
    }
    const url = URL.createObjectURL(selectedFile);
    setObjectPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [selectedFile, isBlob, isImageFile]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    onChange?.(file);
    setSelectedFile?.(file);
  };

  const handleFileRemove = () => {
    onChange?.(null);
    setSelectedFile?.(null);
  };

  const getFileIcon = () => {
    if (!selectedFile) return null;

    const imageSrc = objectPreviewUrl || (isImageFile ? remoteUrl : "");
    if (imageSrc) {
      return <PreviewImg src={imageSrc} alt="" />;
    }

    const ext = fileExtension(selectedFile.name);
    if (ext === "pdf") {
      return <img src={Pdf} width={24} alt="" />;
    }
    if (ext === "doc" || ext === "docx") {
      return <img src={Docx} width={24} alt="" />;
    }
    return <img src={DocFile} width={20} alt="" />;
  };

  return (
    <UploadContainer>
      {selectedFile ? (
        <FileDisplay>
          {getFileIcon()}
          <FileName>{selectedFile.name}</FileName>
          <RemoveButton type="button" onClick={handleFileRemove}>
            <Cancel />
          </RemoveButton>
        </FileDisplay>
      ) : (
        <DefaultUploadBox
          className="defaultUploadBox"
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <FileUpload size={40} />
          <UploadText>
            {t(UploadImage)}
            <br />
            <span>{t(UploadSecTxt)}</span>
          </UploadText>
        </DefaultUploadBox>
      )}

      <input
        id={inputId}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </UploadContainer>
  );
};

export default DocumentUpload;

const UploadContainer = styled.div`
  width: 100%;
  display: flex;
  border: 1px dashed var(--lightGrey2);
  position: relative;
  border-radius: 10px;
  background-color: var(--bg);
  padding: 10px 10px 10px 10px;
`;
const DefaultUploadBox = styled.div`
  display: flex;
  width: 100%;
  height: 90px;
  gap: 10px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding-left: 10px;
  padding-right: 30px;
  font-size: 14px;
  color: var(--text2);
`;
const UploadText = styled.p`
  font-size: 14px;
  margin-top: 7px;
  font-weight: bold;
  color: var(--text2);
  line-height: 18px;
  span {
    font-weight: normal;
    font-size: 14px;
    color: var(--lightGrey2);
  }
`;

const FileDisplay = styled.div`
  display: flex;
  width: 100%;
  height: 90px;
  gap: 10px;
  align-items: center;
  justify-content: center;
`;

const FileName = styled.p`
  font-size: 14px;
  margin-top: 5px;
  color: var(--darkGrey);
  line-height: 18px;
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: none;
  border: none;
  cursor: pointer;
  width: 29px;
  height: 29px;
  background-color: var(--border);
  border-radius: 50%;
  svg {
    width: 14px;
    height: auto;
    [fill] {
      fill: var(--darkGrey);
    }
  }
`;
